/* eslint-disable no-unused-expressions */
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ITreeOptions,
  ITreeState,
  TreeComponent,
  TreeModel,
  TreeNode,
} from '@circlon/angular-tree-component';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

import { FilterAutoCompleteInputComponent, SearchSelector } from '@portal/shared/ui/filter';
import {
  EntityType as EntityTypeEnum,
  EntityType,
  IEntity,
  IEntityAncestors,
  IEntityLightWeight,
  IOrganisation,
} from '@portal/entity-services/interfaces';
import { AuthenticationService } from '@portal/shared/auth/authentication';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { Direction, LanguageHandle } from '@portal/shared/languages';
import { PointerIdentifier } from '../enums/pointer-identifier';
import { ErrorService } from '@portal/shared/ui/form/src/lib/error/error.service';
import { IEntityHierarchy } from '@portal/entity-services/interfaces/src/lib/organisations/interfaces/entity-hierarchy.interface';
import { AuthorizationService } from '@portal/shared/auth/authorization/src';
import uniqBy from 'lodash/uniqBy';
import cloneDeep from 'lodash-es/cloneDeep';
import { MobileViewService } from '@apps/portal/src/app/services/mobile-view.service';
import { SearchParameterList } from '@portal/shared/ui/filter/src/lib/lists/search-parameters.list';
import { SearchParameters } from '@portal/shared/ui/filter/src/lib/enums/search-parameters.enum';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';
import { HttpError } from '@portal/shared/error-handler/src';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle/src';
import { Features } from 'environments/enums/features.enum';

type SelectedNode = IEntity & { children: SelectedNode[] };

const GROUP_TAG = '(Group)';

@UntilDestroy()
@Component({
  selector: 'portal-hierarchy-single-select',
  templateUrl: './hierarchy-single-select.component.html',
  styleUrls: ['./hierarchy-single-select.component.scss'],
  standalone: false,
})
export class HierarchySingleSelectComponent implements OnInit {
  static initialDebounce = 400;
  static reachedConstant = 19;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('control') controlEntityUid: AbstractControl = new UntypedFormControl();
  @Input() organizationGroup: boolean;
  @Input() controlEntityName: AbstractControl = new UntypedFormControl();
  @Input() hasInitialSetter = false;
  @Input() organisations: IOrganisation[] | IEntity[];
  @Input() id: string;
  @Input() displayOptionApplyAndClear: boolean;
  @Input() rootEntityIdForParentUpdate: string;
  @Input() merchantCompanyEntityCount: number;
  @Input() showTrashCanEntities = false;
  @Input() hideOrganisation: IOrganisation;
  @Input() isShowGroup = false; // In front of the group name, we will add a text tag. For example, if a group entity's name is 'ABC Company', it will be displayed as 'ABC Company (Group)' in the UI
  @Output() selectedOrganisation: EventEmitter<IEntity> = new EventEmitter();
  @Output() selectedOrganisationAfterApply: EventEmitter<IEntity> = new EventEmitter();
  @Output() selectedAllDescendantsOfEntity: EventEmitter<string[]> = new EventEmitter();
  @Output() itemsFiltered = new EventEmitter<{ isFirstLoad: boolean; orgs: IOrganisation[] }>();

  @ViewChild('tree') private tree: TreeComponent;
  @ViewChild(forwardRef(() => FilterAutoCompleteInputComponent))
  private searchInput: FilterAutoCompleteInputComponent;
  @ViewChild('orgSingleInput', { read: ElementRef }) orgSingleInput: ElementRef<HTMLInputElement>;
  @ViewChild('orgGroupInput', { read: ElementRef }) orgGroupInput: ElementRef<HTMLInputElement>;
  selectedNode: SelectedNode;
  loading$: Observable<boolean>;
  loadingNewPortion$: Observable<boolean>;
  loadingMore$ = new BehaviorSubject(0);
  hierarchyViewLoading$: Observable<boolean>;
  searchTerm$ = new Subject<string>();
  search$: Observable<IOrganisation[]>;
  nextPage$ = new Subject<number>();
  pagination$: Observable<IOrganisation[]>;
  selectedEntities: IEntity = {};
  selectedAllDescendantsForEntity: IEntity[] = [];
  storedSelectedEntities: IEntity = {};
  selectedActiveEntities: IEntity = {};
  groupEntityRelationships: IEntityLightWeight[] = [];
  entitiesAll: IEntity[] = [];
  entities: IEntity[] = [];
  searchedText = '';
  isOpen = false;
  hasFilteredText = false;
  isFirstLoad = true;
  debounce = 400;
  treeState: ITreeState;
  entityTypes = [
    EntityType.DEVELOPER_COMPANY,
    EntityType.ESTATE_OWNER,
    EntityType.GROUP,
    EntityType.MERCHANT_COMPANY_GROUP,
    EntityType.MERCHANT_COMPANY,
    EntityType.MERCHANT_SITE,
    EntityType.RESELLER,
    EntityType.UNDEFINED,
  ];
  options: ITreeOptions = {
    getChildren: (node: TreeNode) => {
      return this.isEntityTypeGroup && node?.data?.entityType === EntityTypeEnum.GROUP
        ? this.callGetGroupRelationships(node)
        : this.callGetHierarchyView(node);
    },
    useCheckbox: false,
    idField: 'entityUid',
    displayField: 'name',
    rtl:
      this.languageHandle.getCurrentObjectLanguage(this.languageHandle.getBuildLanguage())
        .direction === Direction.RTL,
  };
  isDeviceMobile$: Observable<boolean>;

  isInvalid = ErrorService.isFieldInvalid;
  searchParameterList = this.searchParameters.list;
  searchParameterType = SearchParameters.Name;
  isFriendlyNameFeatureActive = false;
  currentRootEntityId: string;

  get searchPlaceholder(): string {
    return $localize`Search by ${
      this.searchParameterList.find((input) => input.id === this.searchParameterType).text
    }`;
  }
  readonly Directions = Directions;
  private start: number;
  private stopLoading: boolean;
  private loadingStatus = false;
  private isEventExpand = false;
  private selectedPath: string[];
  private getEntities$: Subject<string> = new Subject();
  private getAncestors$: Subject<string> = new Subject();
  private isEntityTypeGroup = false;
  private isScrollEventAdded = false;
  isLastBatch = false;
  limitSelectedEntityElements = 20;

  constructor(
    private organisationService: OrganisationService,
    private authService: AuthenticationService,
    private languageHandle: LanguageHandle,
    private authorizationService: AuthorizationService,
    private mobileViewService: MobileViewService,
    private searchParameters: SearchParameterList,
    private featureToggleService: FeatureToggle,
  ) {
    this.loading$ = organisationService.loading$.pipe(shareReplay(1));
    this.hierarchyViewLoading$ = organisationService.hierarchyViewLoading$.pipe(shareReplay(1));
    this.isDeviceMobile$ = this.mobileViewService.mobileDevice;
    this.loadingNewPortion$ = organisationService.loadingNewPortion$.pipe(
      tap((value) => (this.loadingStatus = value)),
    );
    this.start = 0;
    this.isEntityTypeGroup =
      this.authorizationService['user'].entity.entityType === EntityTypeEnum.GROUP;
  }

  ngOnInit(): void {
    this.isFriendlyNameFeatureActive = this.featureToggleService.isConfigFeatureActive(
      Features.friendlyName,
    );
    this.currentRootEntityId = this.authService.entityUid;
    this.initGetEntities();
    !this.isEntityTypeGroup && this.initGetAncestors();
    this.controlEntityUid.statusChanges.subscribe(() => this.asyncControlsNgFormStates());
    this.controlEntityUid.valueChanges.pipe(debounceTime(200)).subscribe((value: string) => {
      if (value) {
        this.getEntities$.next(value);
      }
    });

    if (this.organisations?.length) {
      const organisation = this.organisations.find((org) => !Object.is(org, null));
      this.selectedEntities = organisation;
      this.controlEntityName.setValue(organisation.name);
    } else if (this.controlEntityUid.value) {
      this.getEntities$.next(this.controlEntityUid.value);
    }

    this.startListeningForSearch();
    this.startListeningForPageChange();
    this.asyncControlsNgFormStates();
    if (this.hasInitialSetter) {
      this.searchTerm$.next('');
    }
  }

  isNodeDisabled(nodeData: any): boolean {
    return nodeData.data?.entityType === EntityTypeEnum.GROUP && this.isEntityTypeGroup;
  }

  onScrolled(event: {
    target: { offsetHeight: number; scrollTop: number; scrollHeight: number };
  }): void {
    if (
      event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 1 &&
      !this.stopLoading &&
      !this.loadingStatus
    ) {
      this.nextPage$.next(this.start);
    }
  }

  isEntityTypeVisible(node: any, isDeviceMobile: boolean | null): boolean {
    return this.organisationService.isEntityTypeVisible(
      node,
      this.isFriendlyNameFeatureActive,
      isDeviceMobile,
    );
  }

  onLoadMore(node: TreeNode): void {
    if (node.data.children.length >= node.data.count) {
      this.isLastBatch = true;
      return;
    }
    if (node.data.count - node.data.children.length <= this.limitSelectedEntityElements) {
      this.isLastBatch = true;
    }
    this.loadingMore$.next(node.id);
    const currentScrollPosition = document.querySelector('tree-viewport')?.scrollTop || 0;
    this.organisationService
      .getHierarchyView(
        node.data.entityUid,
        node.data.children.length === 1 ? 0 : node.data.children.length,
        this.rootEntityIdForParentUpdate
          ? {
              limit: 1000,
              ...(this.merchantCompanyEntityCount
                ? { entityType: EntityType.MERCHANT_COMPANY }
                : {}),
            }
          : this.isShowGroup
          ? { entityType: this.entityTypes.toString() }
          : null,
        undefined,
        !this.isShowGroup,
      )
      .subscribe((value) => {
        node.data.children.push(
          ...(this.selectedNode?.entityUid
            ? value.filter((value1) => !this.selectedPath.includes(value1.entityUid))
            : value),
        );
        this.loadingMore$.next(0);
        this.tree.treeModel.update();
        setTimeout(() => {
          const scrollElement = document.querySelector('tree-viewport');
          if (scrollElement) {
            scrollElement.scrollTop = currentScrollPosition;
          }
        }, 0);
      },
        () => {},
        () => {
          this.loadingMore$.next(0);
        },);
  }

  onActivateItem(selectedEntity: IEntity): void {
    this.selectedEntities = selectedEntity;
    this.storedSelectedEntities = selectedEntity;
    this.isOpen = false;
    this.setControlsValue(this.selectedEntities);
  }

  onDeactivateItem(): void {
    this.asyncControlsNgFormStates();
  }

  removeSelectedParentOrg(entityUid: string): void {
    if (entityUid === this.selectedEntities.entityUid) {
      this.selectedEntities = null;
      this.resetFocus();
      this.onClear();
    }
  }

  onSearch(searchValue = ''): void {
    this.searchedText = searchValue;
    this.start = 0;
    this.searchTerm$.next(searchValue);
  }

  onClickOutside(): void {
    this.isOpen = false;
    this.asyncControlsNgFormStates();
  }

  onToggleExpanded(): void {
    this.isEventExpand = true;
  }

  onToggleDropdown(event: any): void {
    // https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent/pointerId
    if (event.pointerId !== PointerIdentifier.CausingByNotItself && !this.isEventExpand) {
      this.isOpen = !this.isOpen;
      if (!this.entities.length) {
        this.start = 0;
      }
      this.searchedText = '';
      this.searchInput.reset();
      this.searchTerm$.next(this.searchedText);
    }

    this.isEventExpand = false;
    this.asyncControlsNgFormStates();
    if (this.displayOptionApplyAndClear) {
      this.isOpen = true;
    }
  }

  onFilterApplied(applied = false): void {
    this.updateEntities();
    this.start = 0;
    if (!applied) {
      this.selectedOrganisation.emit(this.selectedEntities);
    }
    // If is applied from the template
    if (applied) {
      this.selectedOrganisationAfterApply.emit(this.selectedEntities);
      this.isOpen = false;
    }
  }

  onClear(): void {
    this.selectedEntities = null;
    this.setControlsValue(this.selectedEntities);
    this.onFilterApplied();
    this.asyncControlsNgFormStates();
  }

  onFocusIn(event: FocusEvent, treeModel: TreeModel): void {
    const treeElement = event.currentTarget as HTMLElement;

    if (event.target === treeElement) {
      treeModel.setFocus(true);
      if (!treeModel.focusedNode) {
        treeModel.getFirstRoot()?.focus();
      }
    } else {
      // prevent inputs inside the tree stealing the focus
      treeElement.focus();
    }
  }

  onFocusOut(event: FocusEvent, treeModel: TreeModel): void {
    if (event.target === event.currentTarget) {
      treeModel.setFocus(false);
    }
  }

  private startListeningForPageChange(): void {
    this.pagination$ = this.paginate(this.nextPage$);
    this.pagination$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.entities = [...this.entities, ...data];
      this.entitiesAll = [...this.entitiesAll, ...data];
      if (Object.keys(this.selectedEntities).length) {
        this.updateEntities();
      }
    });
  }

  private paginate(terms: Observable<number>): Observable<IOrganisation[]> {
    return terms.pipe(debounceTime(500)).pipe(
      switchMap((page: number) => {
        this.start = page + 20;
        return this.getEntitiesByName(this.searchedText, true);
      }),
    );
  }

  private getEntitiesByName(term: string, noLoader?: boolean): Observable<IOrganisation[]> {
    const reachedTheEnd = (organisation: IOrganisation[]): boolean => {
      this.hasFilteredText = Boolean(term);
      this.stopLoading = organisation.length < HierarchySingleSelectComponent.reachedConstant;
      return this.stopLoading;
    };

    const isSearchById = this.searchParameterType === SearchParameters.EntityUid;

    // If search term is present and it's by EntityUid
    if (term && isSearchById) {
      return this.organisationService.getByKeyWithAncestors(term, false, null).pipe(
        map((value) => (value ? [value] : [])),
        catchError((err) => {
          if (err.error.status === HttpError.CodeError404) {
            return of([]);
          }
        }),
        tap(reachedTheEnd),
      );
    }

    return forkJoin([
      term ? this.callGetLightWeightWithQuery(term, noLoader) : this.callGetEntity(noLoader),
      this.showTrashCanEntities
        ? this.callGetLightWeightWithQueryOnShowTrashCanEntities(term, noLoader)
        : of([]),
    ])
      .pipe(
        map(([organisationList, trashCanOrganisationList]) => {
          trashCanOrganisationList = trashCanOrganisationList.map((org) => ({
            ...org,
            trashCan: true,
          }));
          return [
            ...(!this.showTrashCanEntities || this.rootEntityIdForParentUpdate
              ? organisationList
              : []),
            ...trashCanOrganisationList,
          ];
        }),
      )
      .pipe(tap(reachedTheEnd));
  }

  private callGetLightWeightWithQueryOnShowTrashCanEntities(term: string, noLoader: boolean) {
    const queryLimit = undefined;
    const isLightweight = true;
    return this.organisationService.getLightWeightWithQuery(
      {
        name: term,
        labels: 'TRASHCAN',
        limit: '1000',
      },
      noLoader,
      this.start,
      queryLimit,
      isLightweight,
    );
  }

  private callGetEntity(noLoader: boolean) {
    return this.organisationService
      .getEntity(
        this.start,
        noLoader,
        this.rootEntityIdForParentUpdate || this.authService.entityUid,
        this.merchantCompanyEntityCount ? { entityType: EntityType.MERCHANT_COMPANY } : null,
        this.isEntityTypeGroup,
      )
      .pipe(
        map(([org]) => {
          if (
            !this.merchantCompanyEntityCount &&
            org.entityUid === this.hideOrganisation?.parentEntityUid
          ) {
            let hasChildren = org.count > 1;
            org = { ...org, count: org.count - 1, hasChildren };
          }
          return [org];
        }),
      );
  }

  private callGetLightWeightWithQuery(term: string, noLoader: boolean) {
    const queryLimit = undefined;
    const isLightweight = true;
    return this.organisationService.getLightWeightWithQuery(
      {
        [this.searchParameterType]: term,
        ...(this.rootEntityIdForParentUpdate
          ? {
              ancestorId: this.rootEntityIdForParentUpdate,
              limit: '1000',
              ...(this.merchantCompanyEntityCount
                ? { entityType: EntityType.MERCHANT_COMPANY }
                : {}),
            }
          : {}),
        ...(this.isShowGroup ? { entityType: this.entityTypes.toString() } : null),
      },
      noLoader,
      this.start,
      queryLimit,
      isLightweight,
    );
  }

  private setSelectedEntities(selectedEntityUid: string): void {
    const selectedForState = {
      [selectedEntityUid]: true,
    };

    this.treeState = this.treeState
      ? {
          ...this.treeState,
          activeNodeIds: selectedForState,
        }
      : { activeNodeIds: selectedForState };
  }

  private startListeningForSearch(): void {
    this.search$ = this.search(this.searchTerm$);
    this.search$.pipe(untilDestroyed(this)).subscribe((entities: IEntity[]) => {
      if (this.isShowGroup) {
        entities = this.addGroupWithName(entities);
      }
      this.entities = entities;
      this.entitiesAll = entities;
      this.emitOrganisations(entities);
      if (Object.keys(this.selectedEntities || {}).length) {
        this.updateEntities();
      }
    });
  }

  private search(terms: Observable<string>): Observable<IOrganisation[]> {
    return terms.pipe(distinctUntilChanged(), debounceTime(this.debounce)).pipe(
      switchMap((term: string) => {
        // Always reset debounce time
        this.debounce = HierarchySingleSelectComponent.initialDebounce;
        return this.getEntitiesByName(term);
      }),
    );
  }

  private updateEntities(): void {
    if (this.selectedEntities?.entityUid) {
      this.treeState.activeNodeIds[this.selectedEntities.entityUid] = true;
      this.tree?.treeModel?.setState(this.treeState);
    }
    this.entities = this.entitiesAll;

    const id = this.selectedEntities?.entityUid;
    if (
      id &&
      id !== (this.rootEntityIdForParentUpdate || this.authService.entityUid) &&
      !this.isEntityTypeGroup
    ) {
      this.getAncestors$.next(id);
    }
  }

  private setControlsValue(organisation: IOrganisation): void {
    this.controlEntityUid.setValue(organisation?.entityUid);
    this.controlEntityName.setValue(organisation?.name);
  }

  private emitOrganisations(orgs: IOrganisation[]): void {
    this.itemsFiltered.emit({ isFirstLoad: this.isFirstLoad, orgs });
    this.isFirstLoad = false;
  }

  private asyncControlsNgFormStates(): void {
    this.controlEntityName.touched
      ? this.controlEntityUid.markAsTouched()
      : this.controlEntityUid.markAsUntouched();
    this.controlEntityUid.disabled
      ? this.controlEntityName.disable()
      : this.controlEntityName.enable();
    this.controlEntityUid.validator
      ? this.controlEntityName.setValidators(this.controlEntityUid.validator)
      : this.controlEntityName.setValidators([]);
    this.controlEntityUid.asyncValidator && !this.rootEntityIdForParentUpdate
      ? this.controlEntityName.setAsyncValidators(this.controlEntityUid.asyncValidator)
      : this.controlEntityName.setAsyncValidators([]);
  }

  private async flatten(data: IEntityAncestors): Promise<any> {
    const result = [];
    while (data) {
      result.push(data);
      if (
        data?.parent?.entityUid === (this.rootEntityIdForParentUpdate || this.authService.entityUid)
      ) {
        break;
      }
      data = data.parent;
    }
    result.map((value) => {
      delete value.parent;
      return value;
    });
    this.selectedPath = result.map((value) => value.entityUid);
    let response = await forkJoin(
      result.map((val) =>
        this.organisationService
          .getTotalCount({
            parentId: val[SearchSelector.EntityUid],
            ...(this.merchantCompanyEntityCount ? { entityType: EntityType.MERCHANT_COMPANY } : {}),
          })
          .pipe(
            map((countData) =>
              val.entityUid === this.hideOrganisation?.parentEntityUid &&
              !this.merchantCompanyEntityCount
                ? { count: countData.count - 1 }
                : countData,
            ),
          ),
      ),
    )
      .pipe(
        map((val) =>
          val.map((totalCountData, index) => ({
            count: totalCountData.count,
            ...result[index],
            hasChildren: totalCountData.count > 0,
            isExpanded: index !== 0,
          })),
        ),
      )
      .toPromise();
    if (this.isShowGroup) {
      response = this.addGroupWithName(response);
    }
    const length = result.length;
    const selectedNode = response[length - 1];
    this.insertChild(selectedNode, response);
    this.selectedNode = selectedNode;

    this.expandTree();
  }

  private insertChild(
    selectedNode: SelectedNode,
    array: SelectedNode[],
    index: number = array.length - 2,
  ): void {
    if (index >= 0 && selectedNode) {
      selectedNode.children = [array[index]];
      this.insertChild(selectedNode.children[0], array, index - 1);
    }
  }

  private expandTree(): void {
    const expandedNodeIds = this.entities[0]?.hasChildren
      ? { [this.entities[0].entityUid]: true }
      : {};
    this.selectedPath.forEach((value, index) => {
      if (!index) {
        return;
      }
      expandedNodeIds[value] = true;
      const someNode = this.tree?.treeModel?.getNodeById(value);
      someNode?.expand();
    });

    this.treeState = {
      ...this.treeState,
      expandedNodeIds,
    };
  }

  private initGetEntities(): void {
    this.getEntities$
      .pipe(
        tap((selectedEntityUid: string) => this.setSelectedEntities(selectedEntityUid)),
        switchMap((selectedEntityUid: string) => {
          return this.organisationService.getByKey(selectedEntityUid, true);
        }),
        untilDestroyed(this),
      )
      .subscribe((organisation: IOrganisation) => {
        if (this.selectedEntities?.entityUid !== organisation.entityUid) {
          if (this.isShowGroup && organisation.entityType === EntityType.GROUP) {
            organisation.name = `${organisation.name} ${GROUP_TAG}`;
          }
          this.controlEntityName.setValue(organisation.name);
        }
        Object.assign(this.selectedEntities, organisation);
        this.onFilterApplied();
        this.asyncControlsNgFormStates();
      });
  }

  private initGetAncestors(): void {
    this.getAncestors$
      .pipe(
        switchMap((entityUid: string) => {
          return this.organisationService.getAncestor(entityUid);
        }),
      )
      .subscribe((value) => {
        this.flatten(value);
      });
  }

  private resetFocus() {
    if (this.tree?.treeModel) {
      this.tree.treeModel.getFocusedNode()?.setIsActive(false);
      this.tree.treeModel.focusedNodeId = null;

      const keys: string[] = this.tree.treeModel.activeNodeIds?.$mobx['getKeys']();
      if (keys?.length) {
        const FIRST_INDEX = 0;
        this.tree.treeModel['setActiveNode'](keys[FIRST_INDEX], null);
      }
    }
  }

  private addGroupWithName(
    list: (SelectedNode | IEntityHierarchy | IEntity)[],
  ): (SelectedNode | IEntityHierarchy | IEntity)[] {
    list = list.map((entity) => {
      if (entity.entityType === EntityType.GROUP && !entity.name.includes(GROUP_TAG)) {
        entity.name = `${entity.name} ${GROUP_TAG}`;
      }
      return entity;
    });
    return list;
  }

  private callGetGroupRelationships(node: TreeNode): Promise<any> {
    return this.organisationService
      .getGroupRelationships(node.id)
      .pipe(
        map((hierarchyList) =>
          hierarchyList.filter((item) => item.entityUid !== this.selectedNode?.entityUid),
        ),
        map((entities) => {
          this.groupEntityRelationships = cloneDeep(uniqBy(entities, SearchSelector.EntityUid));
          this.groupEntityRelationships?.forEach((entity) => {
            if (entity?.entityUid) {
              const node = {
                id: entity?.entityUid,
                data: entity,
              };
              this.setDescendants(node as TreeNode);
            }
          });
          return node.id === this.authService.entityUid
            ? [...(this.selectedNode ? [this.selectedNode] : []), ...entities]
            : entities;
        }),
      )
      .toPromise();
  }

  private setDescendants(node: TreeNode): void {
    this.organisationService.getTotalCount({ parentId: node.id }).subscribe((entityCount) => {
      if (entityCount?.count) {
        const entitiesTree = this.entities;
        entitiesTree[0]?.children?.forEach((entity, index: number) => {
          if (node?.id === entity.entityUid) {
            entity.count = entityCount?.count;
            entity.hasChildren = true;
          }

          if (!this.treeState?.selectedLeafNodeIds) {
            this.treeState = {
              ...this.treeState,
              selectedLeafNodeIds: {
                ...this.treeState?.selectedLeafNodeIds,
                [entity.entityUid]: false,
              },
            };
          } else {
            this.treeState.selectedLeafNodeIds = {
              ...this.treeState.selectedLeafNodeIds,
              [entity.entityUid]: false,
            };
          }
          this.tree?.treeModel?.setState(this.treeState);
          this.tree?.treeModel?.update();
          this.tree?.sizeChanged();
        });
        this.entities = entitiesTree;
      }
    });
  }

  private callGetHierarchyView(node: TreeNode): Promise<any> {
    const onScroll = (event: any): void => {
      const element = event.target;
      const atBottom = Math.floor(element.scrollHeight - element.scrollTop) <= element.clientHeight;
      if (atBottom && !this.loadingMore$.value && !this.isLastBatch) {
        const parentNode = this.tree?.treeModel?.getNodeById(this.currentRootEntityId);
        this.onLoadMore(parentNode);
      }
    };

    if (document.querySelector('tree-viewport') && !this.isScrollEventAdded) {
      document.querySelector('tree-viewport').addEventListener('scroll', onScroll);
      this.isScrollEventAdded = true;
    }
    return this.organisationService
      .getHierarchyView(
        node.id,
        null,
        this.rootEntityIdForParentUpdate
          ? {
              limit: 1000,
              ...(this.merchantCompanyEntityCount
                ? { entityType: EntityType.MERCHANT_COMPANY }
                : {}),
            }
          : this.isShowGroup
          ? { entityType: this.entityTypes.toString() }
          : null,
        undefined,
        !this.isShowGroup,
      )
      .pipe(
        map((hierarchyList) =>
          hierarchyList.filter((item) => item.entityUid !== this.selectedNode?.entityUid),
        ),
        map((value) =>
          node.id === (this.rootEntityIdForParentUpdate || this.authService.entityUid)
            ? [...(this.selectedNode ? [this.selectedNode] : []), ...value]
            : value,
        ),
        map((list: (IEntityHierarchy | IEntity)[]) => {
          if (!this.merchantCompanyEntityCount) {
            list = list.map((item) => {
              return item;
            });
            if (this.isShowGroup) {
              list = this.addGroupWithName(list);
            }
            return list.filter((item) => item.entityUid !== this.hideOrganisation?.entityUid);
          }
          return list;
        }),
      )
      .toPromise();
  }
}
